/** Shared types across the platform web/api (and future native) apps. */

/**
 * Three roles:
 *   user         — end client (the SPA experience)
 *   trainer      — staff; manages their own clients in the admin app (scoped)
 *   supertrainer — trainer + admin (managers); full admin access
 */
export type Role = 'user' | 'trainer' | 'supertrainer';

/** Trainer-tier roles (admin-app access). The SPA treats everyone as a client. */
export const TRAINER_ROLES: Role[] = ['trainer', 'supertrainer'];
export function isTrainerRole(role: string | null | undefined): boolean {
  return role === 'trainer' || role === 'supertrainer';
}

export interface User {
  id: string;
  email: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  role: Role;
  trainer_id?: string | null;
  onboarding_completed: number;
  share_health_with_coach: number;
}

export interface Appointment {
  id: string;
  user_id: string;
  trainer_id: string;
  date: string;
  start_time: string;
  end_time: string;
  appointment_type: string | null;
  notes: string | null;
  status: 'upcoming' | 'cancelled' | 'completed';
  cancelled_at: string | null;
  created_at: string;
  trainer?: { first_name: string | null; last_name: string | null };
  /** Recurring-series grouping. NULL = standalone booking. */
  series_id?: string | null;
  /** 1 once this occurrence has been moved/edited off its series pattern. */
  is_series_exception?: number;
  /** Credits this booking cost (0 = free / legacy). */
  credits_charged?: number;
  /** Times the client moved the date/time (free up to booking free_reschedules). */
  reschedule_count?: number;
}

export interface OpenSlot {
  date: string;
  start_time: string;
  end_time: string;
  trainer_id: string;
  /** Length of this slot in minutes (matches the requested duration). */
  duration: number;
}

export interface Conversation {
  partner_id: string;
  partner_name: string;
  /** 'user' (client) | 'trainer' | 'supertrainer' */
  partner_role?: string;
  partner_avatar?: string | null;
  last_message: string;
  last_message_at: string;
  unread_count: number;
}

export interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  is_read: number;
  created_at: string;
}

export interface Program {
  id: string;
  created_by: string | null;
  name: string;
  description: string | null;
  duration_weeks: number | null;
  workouts_per_week: number | null;
  difficulty: string | null;
  is_template: number;
  is_active: number;
  created_at: string;
  assigned_count?: number;
  workout_count?: number;
}
